import { FC, useState, ImgHTMLAttributes } from 'react';
import { Box, Skeleton, SkeletonProps, Typography } from '@mui/material';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import { useTranslation } from 'react-i18next';

interface ImageWithSkeletonProps extends ImgHTMLAttributes<HTMLImageElement> {
    showSkeleton?: boolean;
    skeletonProps?: SkeletonProps;
    width?: number | string;
    height?: number | string;
}

const ImageWithSkeleton: FC<ImageWithSkeletonProps> = ({
    src,
    alt,
    showSkeleton = true,
    width = '100%',
    height = '100px',
    style,
    skeletonProps,
    ...rest
}) => {
    const { t } = useTranslation();
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);

    const handleLoad = () => {
        setLoaded(true);
    };

    const handleError = () => {
        setError(true);
    };

    return (
        <Box
            sx={{
                position: 'relative',
                width,
                height,
            }}
            {...rest}
        >
            {!loaded && !error && showSkeleton && (
                <Skeleton
                    variant="rectangular"
                    width={width}
                    height={height}
                    sx={{ position: 'absolute', top: 0, left: 0 }}
                    {...skeletonProps}
                />
            )}
            <img
                src={src}
                alt={alt}
                onLoad={handleLoad}
                onError={handleError}
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    opacity: loaded ? 1 : 0,
                    transition: 'opacity 0.5s ease-in-out',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    ...style,
                }}
            />
            {error && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: width,
                        height: height,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <BrokenImageIcon color="disabled" fontSize="large" />
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        {t('failed_to_load_image')}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default ImageWithSkeleton;
